import parsePhoneNumberFromString from 'libphonenumber-js/max';
import type { IntlShape } from 'react-intl';
import type { InferType } from 'yup';
import { bool, object, string } from 'yup';

import type { Country } from '@/types/regionTypes';

import { EMAIL_REGX, MAX_CHAR_PHONE } from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { getPhonePrefixSelectOptByCode } from '@/lib/phonePrefixes/helpers';
import { formMessages } from '@/messages/forms';
import type { PhonePrefix } from '@/views/DeliveryView/helpers';

export const retailerContactUsValidFields: readonly (keyof RetailerContactUsFormValues)[] =
  [
    'title',
    'firstName',
    'lastName',
    'email',
    'phonePrefix',
    'phone',
    'message',
    'captcha',
  ] as const;

export type RetailerContactUsFormValues = InferType<
  ReturnType<typeof contactUsSchema>
>;

export const contactUsSchema = (
  formatMessage: IntlShape['formatMessage'],
  country: Country,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  return object({
    title: getTitleSchema(formatMessage, country.code),
    firstName: getNameSchema(formatMessage, 'first'),
    lastName: getNameSchema(formatMessage, 'last'),
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(''),
    phonePrefix: object({
      value: string().required(requiredMsg),
      code: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).default(getPhonePrefixSelectOptByCode(country.code)),
    phone: string()
      .required(requiredMsg)
      .max(MAX_CHAR_PHONE, formatMessage(formMessages.maxChars))
      .when('phonePrefix', {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix?.value,
        then: (schema) =>
          schema.test(
            'is-valid-phone',
            formatMessage(formMessages.invalidPhoneNumber),
            function (value) {
              const { phonePrefix } = this.parent;

              if (!phonePrefix.value) {
                return false;
              }

              const fullPhoneNumber = `${phonePrefix.value}${value}`;

              const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
              if (!phoneNumber) {
                return false;
              }

              const isValid = phoneNumber.isValid();
              return isValid;
            },
          ),
      })
      .default(''),
    message: string().default('').required(requiredMsg),
    captcha: bool().default(false).oneOf([true], requiredMsg),
  });
};
